import React, { useState } from 'react';
import {
  AccordionItem,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { ComponentRecognizer } from '../ComponentRecognizer/ComponentRecognizer';
import { Html } from '../Html/Html';
import type { AccordionCollapseType, CmsAccordionProps, CmsGeneralAccordionProps } from './types';
import type { HtmlEditorType } from '../Html/types';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';
import { useFilterTimeControl } from '../../hooks/useFilterTimeControl';
import { classes } from '../../utils/classes';

export const CmsGeneralAccordion = ({
  entries,
  highlightOpen = true,
  styleVariant = 'default',
}: CmsGeneralAccordionProps) => {
  const [openedItems, setOpenedItems] = useState<string[]>(
    // already opened items at first render
    entries?.filter((entry) => entry.isOpen).map((entry) => entry.id),
  );

  if (entries?.length && entries.length > 0) {
    return (
      <Accordion
        type="multiple"
        value={openedItems}
        onValueChange={(newOpenedItems) => {
          /**
           * If there is more than one collapse opened (caused by setting multiple collapses to be open on first render),
           * we treat the Accordion as "multiple" and allow multiple Collapses to be opened.
           */
          if (openedItems.length > 1) {
            setOpenedItems(newOpenedItems);
            return;
          }
          // Else treat the Accordion as "single", therefore only one collapse can be opened at a time.

          // get the id of the newly opening collapse
          const newItems = newOpenedItems.filter((newItem) => !openedItems.includes(newItem));

          if (newItems.length > 0) {
            // if we are opening a collapse, set this item as the only open collapse
            setOpenedItems(newItems);
          } else {
            // else set all collapses to be closed.
            setOpenedItems([]);
          }
        }}
      >
        {entries.map(({ headline, id, content }) => (
          <AccordionItem
            noStyle
            key={id}
            value={id}
            className={classes(
              'min-h-6 border-0 border-t-[0.0625rem] border-solid',
              styleVariant === 'dark'
                ? 'bg-color-primary border-color-footer-contrasttext'
                : 'bg-color-background-base border-color-separator',
              highlightOpen &&
                'border-l-color-transparent data-[state=open]:border-l-color-primary border-l-[0.25rem] transition-[border] duration-200 [&]:last-of-type:border-b-[0.0625rem]',
            )}
          >
            {headline && (
              <AccordionSummary
                className={
                  'text-size-boddy1 m-0 py-0.5 text-left md:py-0' +
                  ' sm:flex-row lg:flex-row-reverse lg:justify-end' + // on mobile the icon is right, while on desktop the icon is left
                  ' [&_.icon-emp-cancel]:size-3 [&_.icon-emp-cancel]:rotate-[135deg]' + // rotate the icon to form a "+" when closed and an "x" when opened
                  ' data-[state=open]:font-bold' // if the collapse is opened, use bold font
                }
                icon={classes(
                  'icon-emp-cancel',
                  styleVariant === 'dark'
                    ? 'text-color-primary-contrasttext'
                    : 'text-color-text-dark',
                )}
              >
                <div className="lg:pl-2">{headline}</div>
              </AccordionSummary>
            )}
            <AccordionDetails
              className={classes(
                'p-1.5 lg:p-1 lg:pl-[3.5rem] [a]:decoration-0 [a]:hover:decoration-auto',
                styleVariant === 'dark'
                  ? 'text-color-primary-contrasttext [a]:text-color-primary-contrasttext'
                  : 'text-color-text [a]:text-color-text',
              )}
            >
              {content}
            </AccordionDetails>
          </AccordionItem>
        ))}
      </Accordion>
    );
  }

  return <div />;
};

export const MarkUpData = ({ entries }: CmsGeneralAccordionProps) => {
  if (entries?.length && entries.length > 0) {
    const structuredData = entries.map(({ headline, sanitizedContent }) => ({
      '@type': 'Question',
      name: headline,
      acceptedAnswer: {
        '@type': 'Answer',
        text: sanitizedContent,
      },
    }));

    return (
      <script key="cmsAccordionFaqMarkup" type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: structuredData,
        })}
      </script>
    );
  }

  return null;
};

export const CmsAccordion = ({ data }: CmsAccordionProps) => {
  const filterTimeControl = useFilterTimeControl();
  const accordions = data.paragraph
    ?.filter(filterTimeControl)
    ?.map((item: AccordionCollapseType) => ({
      id: `cms-accordion-${item.id}`,
      headline: item.field_headline,
      isOpen: item.field_is_open,
      simpleContent: item.field_html_editor,
      sanitizedContent: item.field_html_editor_sanitized,
      content: [
        item.field_html_editor && <Html key={item.id} data={item as unknown as HtmlEditorType} />,
        item.paragraph &&
          item.paragraph?.map((subItem) => <ComponentRecognizer item={subItem} key={subItem.id} />),
      ],
    }));

  return (
    <>
      {data?.field_use_as_seo_data && <MarkUpData entries={accordions} />}
      <div className="w-full p-0" style={{ margin: data?.field_margin || 0 }}>
        <CmsGeneralAccordion entries={accordions} />
      </div>
    </>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({ [ComponentTypes.Accordion]: CmsAccordion });
registerComponents({ [ComponentTypes.AccordionLocalizable]: CmsAccordion });
